/**
 * SubscriptionCheckoutModal Component
 * Handles subscription checkout and multiple listing logic
 */

import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { useConfiguration } from '../../../context/configurationContext';
import {
    getSubscriptionTier,
    isValidSubscriptionTier
} from '../../../config/subscriptionConfig';
import { pickCategoryFields } from '../../../util/fieldHelpers';
import { apiBaseUrl } from '../../../util/api';

// Import shared components
import {
    Modal,
    Button,
    PrimaryButton,
    SecondaryButton,
    Loading,
    IconSpinner
} from '../../../components';
import css from './SubscriptionCheckoutModal.module.css';

/**
 * Component to handle subscription checkout flow
 */
const SubscriptionCheckoutModal = props => {
    const {
        listing,
        currentUser,
        isOpen,
        onClose,
        onSubscriptionSuccess,
        onDirectPublish,
        onManageDisableScrolling,
        fetchSubscriptionCheckoutInProgress,
        subscriptionCheckoutError
    } = props; const intl = useIntl();
    const config = useConfiguration();

    const [checkoutInProgress, setCheckoutInProgress] = useState(false);
    const [subscriptionInfo, setSubscriptionInfo] = useState(null);
    const [hasExistingSubscription, setHasExistingSubscription] = useState(false);

    // Get listing category and ad level using the same method as EditListingWizard
    const publicData = listing?.attributes?.publicData || {};
    const categoryKey = config.categoryConfiguration.key;
    const categoryOptions = config.categoryConfiguration.categories;
    const categoriesObj = pickCategoryFields(publicData, categoryKey, 1, categoryOptions);
    const currentCategories = Object.values(categoriesObj);
    const category = currentCategories[0]; // Get the first (and likely only) category
    const adLevel = publicData.listing_level; // Use underscore format as used in other components

    // Get subscription tier information
    const tierInfo = isValidSubscriptionTier(category, adLevel)
        ? getSubscriptionTier(category, adLevel)
        : null;

    // Check for existing subscription when modal opens
    useEffect(() => {
        if (isOpen && currentUser && tierInfo) {
            checkExistingSubscription();
        }
    }, [isOpen, currentUser?.id, tierInfo?.tierKey]);

    const checkExistingSubscription = async () => {
        if (!currentUser?.id || !tierInfo) return;

        try {
            const baseUrl = apiBaseUrl();
            const response = await fetch(
                `${baseUrl}/api/subscription-checkout-info?userId=${currentUser.id.uuid}&category=${category}&adLevel=${adLevel || ''}&listingId=${listing.id.uuid}`,
                {
                    method: 'GET',
                    credentials: 'include', // 🔑 Include authentication cookies!
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (response.ok) {
                const data = await response.json();
                setSubscriptionInfo(data.tierInfo);
                setHasExistingSubscription(data.hasExistingSubscription);
            }
        } catch (error) {
            console.error('Error checking existing subscription:', error);
        }
    };

    const handleCreateCheckout = async () => {
        if (!listing?.id || !currentUser?.id || !tierInfo) return;

        setCheckoutInProgress(true);

        try {
            const baseUrl = apiBaseUrl();
            const response = await fetch(`${baseUrl}/api/create-subscription-checkout`, {
                method: 'POST',
                credentials: 'include', // 🔑 Include authentication cookies!
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    listingId: listing.id.uuid,
                    userId: currentUser.id.uuid,
                    category: category,
                    adLevel: adLevel,
                    successUrl: `${window.location.origin}/manage-subscriptions?payment=success`,
                    cancelUrl: window.location.href
                }),
            });

            const data = await response.json();

            if (response.ok) {
                if (data.useExistingSubscription) {
                    // Listing was published with existing subscription
                    onSubscriptionSuccess('Listing published using existing subscription');
                    onClose();
                } else {
                    // Redirect to Stripe checkout
                    window.location.href = data.checkoutUrl;
                }
            } else {
                console.error('Checkout creation failed:', data.error);
            }
        } catch (error) {
            console.error('Error creating checkout:', error);
        } finally {
            setCheckoutInProgress(false);
        }
    };

    const handleUseExistingSubscription = async () => {
        if (!listing?.id || !subscriptionInfo) return;

        setCheckoutInProgress(true);

        try {
            // This should call the existing subscription logic
            onDirectPublish(listing.id.uuid, true); // true indicates using subscription
            onClose();
        } catch (error) {
            console.error('Error using existing subscription:', error);
        } finally {
            setCheckoutInProgress(false);
        }
    };

    if (!tierInfo) {
        return (
            <Modal
                id="SubscriptionCheckoutModal.invalidTier"
                isOpen={isOpen}
                onClose={onClose}
                onManageDisableScrolling={onManageDisableScrolling}
                usePortal
            >
                <div className={css.modalContent}>
                    <h2>
                        <FormattedMessage id="SubscriptionCheckoutModal.invalidTierTitle" />
                    </h2>
                    <p>
                        <FormattedMessage
                            id="SubscriptionCheckoutModal.invalidTierMessage"
                            values={{ category, adLevel }}
                        />
                    </p>
                    <SecondaryButton onClick={onClose}>
                        <FormattedMessage id="SubscriptionCheckoutModal.closeButton" />
                    </SecondaryButton>
                </div>
            </Modal>
        );
    }

    const isLoading = checkoutInProgress || fetchSubscriptionCheckoutInProgress;

    return (
        <Modal
            id="SubscriptionCheckoutModal.main"
            isOpen={isOpen}
            onClose={onClose}
            onManageDisableScrolling={onManageDisableScrolling}
            usePortal
        >
            <div className={css.modalContent}>
                <h2 className={css.modalTitle}>
                    {hasExistingSubscription ? (
                        <FormattedMessage id="SubscriptionCheckoutModal.existingSubscriptionTitle" />
                    ) : (
                        <FormattedMessage id="SubscriptionCheckoutModal.newSubscriptionTitle" />
                    )}
                </h2>                <div className={css.subscriptionDetails}>
                    <div className={css.subscriptionTier}>
                        <h3>{tierInfo.displayName}</h3>
                        <div className={css.price}>
                            {tierInfo.isFree ? tierInfo.price : `${tierInfo.price}/mo`}
                        </div>
                        <p className={css.description}>
                            {tierInfo.isFree ? 'No payment required' : tierInfo.description}
                        </p>
                    </div>

                    <div className={css.features}>
                        <h4>
                            <FormattedMessage id="SubscriptionCheckoutModal.featuresTitle" />
                        </h4>
                        <ul>
                            {tierInfo.features.map((feature, index) => (
                                <li key={index}>{feature}</li>
                            ))}
                        </ul>
                    </div>
                </div>

                {hasExistingSubscription ? (
                    <div className={css.existingSubscriptionSection}>
                        <div className={css.infoBox}>
                            <FormattedMessage
                                id="SubscriptionCheckoutModal.existingSubscriptionMessage"
                                values={{ tierName: tierInfo.displayName }}
                            />
                        </div>

                        <div className={css.buttonGroup}>
                            <PrimaryButton
                                onClick={handleUseExistingSubscription}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <IconSpinner />
                                ) : (
                                    <FormattedMessage id="SubscriptionCheckoutModal.useExistingButton" />
                                )}
                            </PrimaryButton>

                            <SecondaryButton onClick={onClose} disabled={isLoading}>
                                <FormattedMessage id="SubscriptionCheckoutModal.cancelButton" />
                            </SecondaryButton>
                        </div>
                    </div>
                ) : (
                    <div className={css.newSubscriptionSection}>
                        <div className={css.infoBox}>
                            <FormattedMessage
                                id="SubscriptionCheckoutModal.newSubscriptionMessage"
                                values={{ price: tierInfo.price }}
                            />
                        </div>

                        <div className={css.buttonGroup}>
                            <PrimaryButton
                                onClick={handleCreateCheckout}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <IconSpinner />
                                ) : (
                                    <FormattedMessage
                                        id="SubscriptionCheckoutModal.subscribeButton"
                                        values={{ price: tierInfo.price }}
                                    />
                                )}
                            </PrimaryButton>

                            <SecondaryButton onClick={onClose} disabled={isLoading}>
                                <FormattedMessage id="SubscriptionCheckoutModal.cancelButton" />
                            </SecondaryButton>
                        </div>
                    </div>
                )}

                {subscriptionCheckoutError && (
                    <div className={css.error}>
                        <FormattedMessage id="SubscriptionCheckoutModal.checkoutError" />
                    </div>
                )}
            </div>
        </Modal>
    );
}; export default SubscriptionCheckoutModal;
